import { useMutation, useQuery } from '@apollo/client'
import {
  ADD_TRANSACTION_MEMBER,
  GET_TRANSACTION_LOAN_ROLES,
  GET_TRANSACTION_MEMBERS,
  GET_TRANSACTION_MEMBERS_HISTORY,
  LOOKUP_PERSON,
  REMOVE_TRANSACTION_MEMBER,
  SEARCH_MEMBERS,
  UPDATE_PERSON,
  UPDATE_TRANSACTION_MEMBER,
} from './transaction-members'
import type {
  AddTransactionMemberInput,
  AddTransactionMemberResponse,
  GetTransactionLoanRolesResponse,
  GetTransactionMembersHistoryResponse,
  GetTransactionMembersResponse,
  LookupPersonInput,
  LookupPersonResponse,
  RemoveTransactionMemberInput,
  RemoveTransactionMemberResponse,
  SearchMembersInput,
  SearchMembersResponse,
  UpdatePersonInput,
  UpdatePersonResponse,
  UpdateTransactionMemberInput,
  UpdateTransactionMemberResponse,
} from './transaction-members'

type GetMembersVariables = {
  input: {
    transaction_id: number
    role_code?: string[]
    state?: 'active' | 'inactive' | 'all'
    include_account_data?: boolean
    include_deleted?: boolean
  }
}

/**
 * Builds the GetTransactionMembers variables.
 *
 * `include_deleted` is omitted entirely unless it is true. GraphQL rejects input
 * fields the server's schema does not declare, so sending `false` would break
 * every members query — active tab, history, and mutation refetches — against a
 * ux-gql that has not yet deployed the field.
 */
export const toGetMembersVariables = ({
  transactionId,
  roleCode,
  state,
  includeAccountData = true,
  includeDeleted = false,
}: {
  transactionId: number
  roleCode?: string[]
  state?: 'active' | 'inactive' | 'all'
  includeAccountData?: boolean
  includeDeleted?: boolean
}): GetMembersVariables => ({
  input: {
    transaction_id: transactionId,
    role_code: roleCode,
    state,
    include_account_data: includeAccountData,
    ...(includeDeleted ? { include_deleted: true } : {}),
  },
})

export const toSearchVariables = (input: SearchMembersInput) => ({
  input: {
    query: input.query,
    first_name: input.firstName,
    last_name: input.lastName,
    email: input.email,
    phone: input.phone,
    limit: input.limit,
    offset: input.offset,
    auth0_roles: input.auth0Roles,
    auth0_roles_match: input.auth0RolesMatch,
    do_not_include_inactive: input.doNotIncludeInactive,
    include_deleted: input.includeDeleted,
    person_ids: input.personIds,
    account_ids: input.accountIds,
  },
})

const toAddMemberVariables = (input: AddTransactionMemberInput) => ({
  input: {
    transaction_id: input.transactionId,
    role_code: input.roleCode,
    account_id: input.accountId,
    person_id: input.personId,
    agent_transaction_deal_id: input.dealId,
    active_start_date: input.activeStartDate,
    active_end_date: input.activeEndDate,
  },
})

const toUpdateMemberVariables = (input: UpdateTransactionMemberInput) => ({
  input: {
    transaction_id: input.transactionId,
    member_id: input.memberId,
    agent_transaction_deal_id: input.dealId,
    active_end_date: input.activeEndDate,
  },
})

const toRemoveMemberVariables = (input: RemoveTransactionMemberInput) => ({
  input: {
    transaction_id: input.transactionId,
    member_id: input.memberId,
  },
})

const DIRECT_CONTEXT = { clientName: 'direct' as const }
const PROXY_CONTEXT = { clientName: 'proxy' as const }

const toUpdatePersonVariables = (input: UpdatePersonInput) => ({
  input: {
    person_id: input.personId,
    ...(input.firstName !== undefined && { first_name: input.firstName }),
    ...(input.lastName !== undefined && { last_name: input.lastName }),
    ...(input.phoneNumber !== undefined && { phone_number: input.phoneNumber }),
    ...(input.mobileNumber !== undefined && { mobile_number: input.mobileNumber }),
  },
})

const getRefetches = (transactionId: number) => [
  {
    query: GET_TRANSACTION_MEMBERS,
    variables: toGetMembersVariables({ transactionId, state: 'active' }),
    context: PROXY_CONTEXT,
  },
  {
    query: GET_TRANSACTION_MEMBERS_HISTORY,
    variables: toGetMembersVariables({ transactionId, state: 'all' }),
    context: PROXY_CONTEXT,
  },
]

/**
 * Hook to fetch active transaction members
 */
/**
 * @param state Membership lifecycle filter (active_end_date).
 * @param options.includeDeleted Also return soft-deleted members. Internal debug
 *   only — the API requires an engineering/product role and returns 403 otherwise.
 */
export const useGetTransactionMembers = (
  transactionId: number,
  state: 'active' | 'inactive' | 'all' = 'active',
  { includeAccountData = true, includeDeleted = false }: { includeAccountData?: boolean; includeDeleted?: boolean } = {}
) => {
  const query = useQuery<GetTransactionMembersResponse>(GET_TRANSACTION_MEMBERS, {
    variables: toGetMembersVariables({ transactionId, state, includeAccountData, includeDeleted }),
    context: PROXY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    skip: !transactionId,
  })

  return {
    members: query.data?.transactionMembers ?? [],
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch loan roles (internal Knock employees on the primary loan).
 */
export const useGetTransactionLoanRoles = (transactionId?: number) => {
  const query = useQuery<GetTransactionLoanRolesResponse>(GET_TRANSACTION_LOAN_ROLES, {
    variables: { input: { transaction_id: transactionId } },
    context: PROXY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    skip: !transactionId,
  })

  return {
    loanRoles: query.data?.transactionLoanRoles ?? [],
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to fetch transaction members history
 */
export const useGetTransactionMembersHistory = (transactionId?: number, includeAccountData = true) => {
  const query = useQuery<GetTransactionMembersHistoryResponse>(GET_TRANSACTION_MEMBERS_HISTORY, {
    variables: toGetMembersVariables({ transactionId: transactionId as number, state: 'all', includeAccountData }),
    context: PROXY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    skip: !transactionId,
  })

  return {
    history: query.data?.transactionMembersHistory ?? [],
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to search for members/people
 */
export const useSearchMembers = () => {
  const query = useQuery<SearchMembersResponse>(SEARCH_MEMBERS, {
    context: PROXY_CONTEXT,
    skip: true, // Manual queries only
  })

  const searchMembers = async (input: SearchMembersInput) => {
    const result = await query.refetch(toSearchVariables(input))
    return result.data?.searchMembers?.members ?? []
  }

  return {
    searchMembers,
    loading: query.loading,
    error: query.error,
    results: query.data?.searchMembers?.members ?? [],
  }
}

/**
 * Hook to add a member to a transaction
 */
export const useAddTransactionMember = () => {
  const [mutate, { loading, error }] = useMutation<AddTransactionMemberResponse>(ADD_TRANSACTION_MEMBER, {
    refetchQueries: [GET_TRANSACTION_MEMBERS, GET_TRANSACTION_MEMBERS_HISTORY],
    context: PROXY_CONTEXT,
  })

  const addMember = async (input: AddTransactionMemberInput) => {
    const result = await mutate({
      variables: toAddMemberVariables(input),
      refetchQueries: getRefetches(input.transactionId),
      context: PROXY_CONTEXT,
    })

    if (!result.data?.addTransactionMember.success) {
      throw new Error(result.data?.addTransactionMember.message ?? 'Failed to add member')
    }

    return result.data.addTransactionMember.member
  }

  return {
    addMember,
    loading,
    error,
  }
}

/**
 * Hook to update a transaction member
 */
export const useUpdateTransactionMember = () => {
  const [mutate, { loading, error }] = useMutation<UpdateTransactionMemberResponse>(UPDATE_TRANSACTION_MEMBER, {
    refetchQueries: [GET_TRANSACTION_MEMBERS, GET_TRANSACTION_MEMBERS_HISTORY],
    context: PROXY_CONTEXT,
  })

  const updateMember = async (input: UpdateTransactionMemberInput) => {
    const result = await mutate({
      variables: toUpdateMemberVariables(input),
      refetchQueries: getRefetches(input.transactionId),
      context: PROXY_CONTEXT,
    })

    if (!result.data?.updateTransactionMember.success) {
      throw new Error(result.data?.updateTransactionMember.message ?? 'Failed to update member')
    }

    return result.data.updateTransactionMember.member
  }

  return {
    updateMember,
    loading,
    error,
  }
}

/**
 * Hook to remove a member from a transaction
 */
export const useRemoveTransactionMember = () => {
  const [mutate, { loading, error }] = useMutation<RemoveTransactionMemberResponse>(REMOVE_TRANSACTION_MEMBER, {
    refetchQueries: [GET_TRANSACTION_MEMBERS, GET_TRANSACTION_MEMBERS_HISTORY],
    context: PROXY_CONTEXT,
  })

  const removeMember = async (input: RemoveTransactionMemberInput) => {
    const result = await mutate({
      variables: toRemoveMemberVariables(input),
      refetchQueries: getRefetches(input.transactionId),
      context: PROXY_CONTEXT,
    })

    if (!result.data?.removeTransactionMember.success) {
      throw new Error(result.data?.removeTransactionMember.message ?? 'Failed to remove member')
    }

    return result.data.removeTransactionMember.success
  }

  return {
    removeMember,
    loading,
    error,
  }
}

/**
 * Hook to update a person's first name, last name, phone, and mobile number
 */
export const useUpdatePerson = () => {
  const [mutate, { loading, error }] = useMutation<UpdatePersonResponse>(UPDATE_PERSON, {
    context: DIRECT_CONTEXT,
  })

  const updatePerson = async (input: UpdatePersonInput) => {
    const hasModifiedFields =
      input.firstName !== undefined ||
      input.lastName !== undefined ||
      input.phoneNumber !== undefined ||
      input.mobileNumber !== undefined
    if (!hasModifiedFields) return null

    const result = await mutate({
      variables: toUpdatePersonVariables(input),
      refetchQueries: [
        {
          query: LOOKUP_PERSON,
          variables: toLookupPersonVariables({
            personId: input.personId,
            includeLeadsCheck: true,
            includeInactive: true,
          }),
          context: DIRECT_CONTEXT,
        },
      ],
      context: DIRECT_CONTEXT,
    })

    return result.data?.updatePerson ?? null
  }

  return {
    updatePerson,
    loading,
    error,
  }
}

const toLookupPersonVariables = (input: LookupPersonInput) => ({
  input: {
    person_id: input.personId,
    account_id: input.accountId,
    include_leads_check: input.includeLeadsCheck,
    include_inactive: input.includeInactive,
  },
})

type LookupPersonOptions = {
  context?: { clientName: 'proxy' | 'direct' }
}

/**
 * Hook to lookup a person by person_id or account_id
 */
export const useLookupPerson = (input: LookupPersonInput | null, options?: LookupPersonOptions) => {
  const shouldSkip = !input || (!input.personId && !input.accountId)

  const query = useQuery<LookupPersonResponse>(LOOKUP_PERSON, {
    variables: input ? toLookupPersonVariables(input) : undefined,
    context: options?.context ?? PROXY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    skip: shouldSkip,
  })

  return {
    person: query.data?.lookupPerson ?? null,
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  }
}
